import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import { Map as MapIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import type { OrgEventTriggeringItem } from 'hooks/orgEvents/orgEventAutomationHooks';
import { useUscgMapContext } from '../LayersTab/UscgMapContext';
import type { ArcgisMapView } from '../LayersTab/utils/arcgisView';
import { buildFallbackFeatureGraphic } from '../LayersTab/utils/focusFeatureOnMap';
import { collectFeatureLayers } from '../LayersTab/utils/useExternalLayerTimeExtent';
import { groupTriggeringItems } from './groupTriggeringItems';

function safeField(name: string | undefined): string {
  return name && /^[A-Za-z_][A-Za-z0-9_]*$/.test(name) ? name : 'OBJECTID';
}

function featureWhere(field: string, id: string): string {
  const trimmed = id.trim();
  if (/^-?\d+(\.\d+)?$/.test(trimmed)) return `${field} = ${trimmed}`;
  return `${field} = '${trimmed.replace(/'/g, "''")}'`;
}

async function liveGraphic(
  mapView: ArcgisMapView | null,
  item: OrgEventTriggeringItem
) {
  const serviceUrl = item.serviceUrl?.trim();
  if (!mapView?.map || !serviceUrl) return null;
  const wanted = serviceUrl.replace(/\/$/, '').toLowerCase();
  const layer = collectFeatureLayers(mapView.map.layers.toArray()).find(
    (candidate) =>
      (candidate.url || '').replace(/\/$/, '').toLowerCase() === wanted
  );
  if (!(layer instanceof FeatureLayer)) return null;
  try {
    const { features } = await layer.queryFeatures({
      where: featureWhere(safeField(item.featureIdField), item.id),
      returnGeometry: true,
      outFields: ['*'],
      num: 1,
    });
    const hit = features[0];
    return hit?.geometry ? hit : null;
  } catch {
    return null;
  }
}

/**
 * Features that fired the rule, grouped as Items Near [asset], each with a map
 * zoom.
 */
export function TriggeringMapItems({
  items,
}: {
  items?: OrgEventTriggeringItem[] | null;
}) {
  const groups = groupTriggeringItems(items);
  const { mapView, focusFeatureOnMap } = useUscgMapContext();
  if (groups.length === 0) return null;

  const focusItem = async (item: OrgEventTriggeringItem) => {
    const live = await liveGraphic(mapView, item);
    const graphic =
      live ??
      buildFallbackFeatureGraphic({
        geometry: item.geometry,
        title: item.name,
        attributes: { name: item.name },
      });
    if (!graphic) return;
    await focusFeatureOnMap({ graphic });
  };

  return (
    <div className="tw:min-w-0 tw:space-y-3">
      {groups.map((group) => (
        <div key={group.key} className="tw:min-w-0">
          <p className="tw:m-0 tw:font-medium tw:text-foreground">
            {group.heading}
          </p>
          <div className="tw:mt-2 tw:space-y-1">
            {group.items.map((item) => (
              <div
                key={`${group.key}:${item.serviceUrl ?? ''}:${item.id}`}
                className="tw:flex tw:min-w-0 tw:items-center tw:justify-between tw:gap-2"
              >
                <span className="tw:min-w-0 tw:truncate tw:text-sm tw:text-foreground">
                  {item.name}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  aria-label={`Show ${item.name} on map`}
                  onClick={() => {
                    void focusItem(item);
                  }}
                >
                  <MapIcon className="tw:h-4 tw:w-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
